/**
 * @fileoverview MonUI - Accessible UI Component Library
 * @version 1.0.0
 * @author edlio
 * 
 * A lightweight JavaScript library for creating accessible UI components
 * including tabs, accordions, and toggle panels. Built with ARIA support
 * and keyboard navigation.
 * 
 * Features:
 * - Tabs (horizontal/vertical)
 * - Accordions (single/multiple panel open)
 * - Toggle Single Panel
 * - Keyboard Navigation
 * - ARIA Attributes
 * - Video Handling
 * 
 * @example
 * // Initialize tabs
 * const tabs = new MonUI('#tabs-container')
 * tabs.tabs({ vertical: true })
 * 
 * // Initialize accordion
 * const accordion = new MonUI('#accordion-container')
 * accordion.accordion({ collapsible: true })
 */
'use strict'
class MonUI {
  /**
   * Creates a new MonUI instance
   * @param {string} idName - CSS selector for the container element
   */
  constructor(idName) {
    this.idName = idName

    /** @type {Object} Default initialization options for all components */
    this.defaultOptions = {
      start: 0 // Index of initially active panel
    }
    
    /** @type {Object} Default options specific to tabs component */
    this.defaultTabsOptions = {
      active: 'click', // Trigger event type
      vertical: false // Orientation of tabs
    }
    
    /** @type {Object} Default options specific to accordion component */
    this.defaultAccordionOptions = {
      collapsible: false // Whether all panels can be closed
    }
    
    /** @type {Object} Default options for toggle panel component */
    this.defaultToggleOptions = {
      initClosed: false // Initial state of the panel
    }
    
    /** @type {Object} Selector mappings for UI elements */
    this.uiElements = {
      // Common selectors used across components
      button: '.monui-button',
      panel: '.monui-panel',

      // Tabs-specific selectors
      tabsList: '.monui-tabs-list'
    }
  }

  /**
   * Stops video playback in panels
   * Handles both HTML5 video elements and embedded iframes (YouTube, Vimeo)
   * @param {HTMLElement} panel - The panel containing videos to stop
   * @private
   */
  _stopVideos(panel) {
    const videos = panel.querySelectorAll('video, iframe[src*="youtube"], iframe[src*="vimeo"]')
    videos.forEach(video => {
      if (video.tagName === 'VIDEO') {
        video.pause()
      } else {
        // Handle iframes by resetting source
        const src = video.src
        video.src = src
      }
    })
  }
  
  /**
   * Closes all panels except the specified index
   * @param {number} index - Index of the panel to keep open
   * @param {string} buttonAria - ARIA attribute to toggle ('aria-selected' for tabs, 'aria-expanded' for accordions)
   * @param {HTMLElement} nodes - Container element with buttons and panels
   * @param {Function} [callback=null] - Optional callback to execute after closing
   * @private
   */
  _closeNodes(index, buttonAria, nodes, callback = null) {
    // Update button states
    nodes.querySelectorAll(this.uiElements.button).forEach((btn, i) =>
      btn.setAttribute(buttonAria, index === i)
    )
    // Update panel visibility
    nodes.querySelectorAll(this.uiElements.panel).forEach((panel, i) => {
      panel.setAttribute('aria-hidden', index !== i)
      this._stopVideos(panel)
    })
    if (typeof callback === 'function') callback()
  }
  
  /**
   * Toggles a single panel's visibility state
   * @param {HTMLElement} button - The button that controls the panel
   * @param {string} buttonAria - ARIA attribute to toggle
   * @param {Function} [callback=null] - Optional callback to execute after toggling
   * @private
   */
  _toggleNode(button, buttonAria, callback = null) {
    // aria-controls required for accessibility and have to be set initially in html
    if(button.getAttribute('aria-controls') === null) return console.log('No aria-controls found in button')
    const expanded = button.getAttribute(buttonAria) === 'true'
    const panel = document.querySelector('#' + button.getAttribute('aria-controls'))
    button.setAttribute(buttonAria, !expanded)
    panel.setAttribute('aria-hidden', expanded)
    if (typeof callback === 'function') callback()
    this._stopVideos(panel)
  }
  
  _keypressHandler(nodes, tabsOptions = {}) {
    const newOptions = {
      prev: 'ArrowLeft',
      next: 'ArrowRight',
      ...tabsOptions
    }

    nodes.addEventListener('keydown', e => {
      let flag = false

      const buttons = Array.from(nodes.querySelectorAll('button'))
      const firstButton = buttons[0]
      const lastButton = buttons[buttons.length - 1]

      function handlePrev() {
        const i = buttons.indexOf(document.activeElement)
        if(i > 0) return buttons[i - 1].focus()
      }
      
      function handleNext() {
        const i = buttons.indexOf(document.activeElement)
        if(i < (buttons.length - 1)) return buttons[i + 1].focus()
      }

      const handleTab = () => {
        const buttons = nodes.querySelectorAll('[aria-selected="true"]')
        const actives = nodes.querySelectorAll('[aria-hidden="false"] a')

        if(actives.length === 0) return false
        if(buttons.length === 0) return false

        const isFirstButton = document.activeElement === buttons[0]
        const isFirstActive = document.activeElement === actives[0]

        if(isFirstButton && !e.shiftKey) {
          actives[0].focus()
          return isFirstButton
        }

        if(isFirstActive && e.shiftKey) {
          buttons[0].focus()
          return isFirstActive
        }

        return false
      }

      switch(e.key) {
        case 'Home':
          flag = true
          firstButton.focus()
          break
        
        case 'End':
          flag = true
          lastButton.focus()
          break

        case newOptions.prev:
          flag = true
          handlePrev()
          break

        case newOptions.next:
          flag = true
          handleNext()
          break

        case 'Tab':
          flag = handleTab()
          break

        default: break
      }

      if(flag) {
        e.stopPropagation()
        e.preventDefault()
      }
    })  
  }
  
  /**
   * Initializes a tabbed interface
   * @param {Object} [options={}] - Configuration options
   * @param {number} [options.start=0] - Index of initially active tab
   * @param {string} [options.active='click'] - Event type to trigger tab switching
   * @param {boolean} [options.vertical=false] - Whether tabs are vertically oriented
   * @throws {Error} When no container element is found with the provided ID
   * @example
   * const tabs = new MonUI('#my-tabs')
   * tabs.tabs({ vertical: true })
   */
  tabs(options = {}) {
    const newOptions = {
      ...this.defaultOptions,
      ...this.defaultTabsOptions,
      ...options
    }
  
    const ui = document.querySelector(this.idName)
    if(ui === null) return console.log('No id found in tabs container')
    const tabList = ui.querySelector(this.uiElements.tabsList)
    const buttons = ui.querySelectorAll(this.uiElements.button)
    const panels = ui.querySelectorAll(this.uiElements.panel)
    
    tabList.setAttribute('role','tablist')
    ui.querySelector(this.uiElements.tabsList).setAttribute('aria-orientation', newOptions.vertical ? 'vertical' : 'horizontal')
    ui.style.setProperty('--active-panel', newOptions.start)
    ui.style.setProperty('--total-panels', panels.length)
    
    buttons.forEach((button, i) => {
      button.setAttribute('aria-selected', i === newOptions.start)
      button.setAttribute('role', 'tab')
      button.addEventListener(newOptions.active, () => this._closeNodes(i, 'aria-selected', ui))
    })

    panels.forEach((panel, i) => {
      panel.setAttribute('aria-hidden', i !== newOptions.start)
      panel.setAttribute('role', 'tabpanel')
    })

    this._keypressHandler(ui, newOptions.vertical ? { prev: 'ArrowUp', next: 'ArrowDown'} : {})

    console.log('MonUI: init ui-tabs in ' + this.idName + ' at: ' + window.innerWidth + 'px')
  }
  
  /**
   * Initializes an accordion interface
   * @param {Object} [options={}] - Configuration options
   * @param {number} [options.start=0] - Index of initially expanded panel
   * @param {boolean} [options.collapsible=false] - Whether all panels can be collapsed
   * @throws {Error} When no container element is found with the provided ID
   * @example
   * const accordion = new MonUI('#my-accordion')
   * accordion.accordion({ collapsible: true })
   */
  accordion(options = {}) {
    const newOptions = {
      ...this.defaultOptions,
      ...this.defaultAccordionOptions,
      ...options
    }
    
    const ui = document.querySelector(this.idName)
    if(ui === null) return console.log('No id found in accordion container')
    
    const buttons = ui.querySelectorAll(this.uiElements.button)
    const panels = ui.querySelectorAll(this.uiElements.panel)
    ui.style.setProperty('--active-panel', newOptions.start)
    ui.style.setProperty('--total-panels', panels.length)
    
    buttons.forEach((button, i) => {
      const callback = () => ui.style.setProperty('--active-panel', i)
      button.setAttribute('aria-expanded', i === newOptions.start)
      button.addEventListener('click', () => {
        if(newOptions.collapsible) return this._toggleNode(button, 'aria-expanded', callback)
        return this._closeNodes(i, 'aria-expanded', ui, callback)
      })  
    })
    
    panels.forEach((panel, i) => {
      panel.setAttribute('role', 'region')
      panel.setAttribute('aria-hidden', i !== newOptions.start)
    })
    console.log('MonUI: init ui-accordion in ' + this.idName + ' at: ' + window.innerWidth + 'px')
  }

  /**
   * Initializes a toggle panel
   * @param {Object} [options={}] - Configuration options
   * @param {boolean} [options.initClosed=false] - Whether panel starts closed
   * @throws {Error} When no container element is found with the provided ID
   * @example
   * const toggle = new MonUI('#my-toggle')
   * toggle.togglePanel({ initClosed: true })
   */
  togglePanel(options = {}) {
    const newOptions = {
      ...this.defaultOptions,
      ...this.defaultToggleOptions,
      ...options
    }
    
    const ui = document.querySelector(this.idName)
    if(ui === null) return console.log('No id found in toggle container')
      
    const button = ui.querySelector(this.uiElements.button)
    const panel = ui.querySelector(this.uiElements.panel)
    
    button.setAttribute('aria-expanded', !newOptions.initClosed)
    button.addEventListener('click', () => this._toggleNode(button, 'aria-expanded'))
    
    panel.setAttribute('role', 'region')
    panel.setAttribute('aria-hidden', newOptions.initClosed)

    ui.addEventListener('keydown', e => {
      if(button.getAttribute('aria-expanded') === 'false') return
      if(e.key == 'Escape') {
        e.preventDefault()
        return this._toggleNode(button, 'aria-expanded')
      }
      if(e.key == 'Tab') {
        const focusables = ui.querySelectorAll('button, a, input')
        const first = focusables[0]
        const last = focusables[focusables.length - 1]

        if(!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          return first.focus()
        }
        if(e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          return last.focus()
        }
      }
    })
    console.log('MonUI: init ui-toggle-panel in ' + this.idName + ' at: ' + window.innerWidth + 'px')
  }
}